import { endpoints } from '@/services/endpoints';
import { axiosGetRequest } from '@/services/Request';
import { useQuery } from '@tanstack/react-query';
import { AppCard } from '@/components/ui/AppCard';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const COLORS = {
  linked: '#0D9373',
  total: '#3B82F6',
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-coal-200 bg-white p-3 shadow-lg text-sm">
        <p className="mb-1 font-semibold text-coal-800 capitalize">{label}</p>
        {payload.map((entry: any) => (
          <div key={entry.name} className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: entry.color }}
            />
            <span className="text-coal-600 capitalize">{entry.name}:</span>
            <span className="font-medium text-coal-900">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function TicketsWithLinkedEntities() {
  const { data: linkedData, isLoading } = useQuery({
    queryKey: ['ticketsWithLinkedEntities'],
    queryFn: () => axiosGetRequest(endpoints.getTicketsWithLinkedEntities),
    select: res => {
      // Normalise the response — the API may return an array of objects like:
      // [{ type: 'book', total: 12, linked: 7 }, ...]
      // or a flat object like { book: { total: 12, linked: 7 }, ... }
      const raw = res?.data ?? res;
      if (Array.isArray(raw)) return raw;
      // Convert object form to array
      return Object.entries(raw as Record<string, any>).map(([key, val]) => ({
        type: key,
        ...(typeof val === 'object' ? val : { linked: val }),
      }));
    },
  });

  return (
    <AppCard>
      <h5 className="mb-4 text-base font-semibold text-coal-900">
        Tickets with Linked Entities
      </h5>
      {isLoading ? (
        <div className="p-4">
          <Skeleton className="h-64 w-full rounded-md bg-coal-200" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={linkedData ?? []}
            margin={{ top: 10, right: 16, left: 0, bottom: 20 }}
            barCategoryGap="30%"
            barGap={4}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="type"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              fontSize={11}
              interval={0}
              tickFormatter={v => {
                const s = String(v);
                return s.length > 12 ? s.slice(0, 12) + '…' : s;
              }}
            />
            <YAxis tickLine={false} axisLine={false} width={38} fontSize={11} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
              formatter={value => (
                <span style={{ color: '#5A6270', textTransform: 'capitalize' }}>{value}</span>
              )}
            />
            <Bar
              dataKey="total"
              name="total"
              fill={COLORS.total}
              radius={[5, 5, 0, 0]}
              maxBarSize={48}
            />
            <Bar
              dataKey="linked"
              name="linked"
              fill={COLORS.linked}
              radius={[5, 5, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </AppCard>
  );
}
